import { Dialog, DialogTitle, IconButton, DialogContent, TextField, DialogActions, Button } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import React from "react";

type AddMoneyDialogProps = {
  open: boolean;
  accent: string;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (amount: number, note: string) => void;
};

type AddMoneyDialogState = {
  amount: string;
  note: string;
};

export default class AddMoneyDialog extends React.Component<AddMoneyDialogProps, AddMoneyDialogState> {
  constructor(props: AddMoneyDialogProps) {
    super(props);
    this.state = { amount: "", note: "" };
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidUpdate(prevProps: AddMoneyDialogProps) {
    if (!prevProps.open && this.props.open) this.setState({ amount: "", note: "" });
  }

  private parseMoney(input: string) {
    const normalized = (input || "").trim().replace(",", ".");
    const n = Number(normalized);
    if (!Number.isFinite(n)) return null;
    return n;
  }

  private handleSubmit() {
    const amount = this.parseMoney(this.state.amount);
    if (amount === null || amount <= 0) return;
    const note = (this.state.note || "").trim();
    this.props.onSubmit(amount, note);
  }

  render() {
    const { open, onClose, accent, isLoading } = this.props;
    const amountParsed = this.parseMoney(this.state.amount);
    const canSubmit = amountParsed !== null && amountParsed > 0 && !isLoading;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: `1px solid ${accent}33`,
            boxShadow: "0 18px 60px rgba(0,0,0,.22)",
            overflow: "hidden",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0,0,0,.45)",
              backdropFilter: "blur(5px)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            py: 2,
            px: 2.5,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            background: "linear-gradient(180deg, rgba(199,150,86,0.10), rgba(255,255,255,0))",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 12,
                background: `${accent}14`,
                border: `1px solid ${accent}33`,
                display: "grid",
                placeItems: "center",
                color: accent,
              }}
            >
              <AttachMoneyIcon fontSize="small" />
            </div>
            Add money
          </div>

          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2.5, display: "grid", gap: 1.6 }}>
          <TextField
            autoFocus
            label="Amount (€)"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={this.state.amount}
            onChange={(e) => this.setState({ amount: e.target.value })}
            fullWidth
            helperText="Tip: you can type 12,50 or 12.50"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 3 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: `${accent}40` },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accent },
            }}
          />

          <TextField
            label="Note (optional)"
            value={this.state.note}
            onChange={(e) => this.setState({ note: e.target.value })}
            fullWidth
            placeholder="e.g. Shift tips Friday"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 3 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.12)" },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accent },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0, gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              px: 2,
              border: `1px solid ${accent}55`,
              color: "#1a1a1a",
            }}
            variant="outlined"
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={this.handleSubmit}
            disabled={!canSubmit}
            sx={{
              textTransform: "none",
              fontWeight: 900,
              borderRadius: 3,
              px: 2.2,
              backgroundColor: accent,
              "&:hover": { backgroundColor: "#b78445" },
            }}
          >
            Deposit
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}